import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import api from '../lib/api';
import { 
  User, Mail, Phone, Baby, Calendar, Heart, 
  ChevronRight, ChevronLeft, CreditCard, CheckCircle,
  GraduationCap, Clock, Users
} from 'lucide-react';

const formatPrice = (price) => `£${(price || 0).toFixed(2)}`;

const PublicRegister = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [step, setStep] = useState(1);
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form states
  const [guardianForm, setGuardianForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: ''
  });
  
  const [childForm, setChildForm] = useState({
    first_name: '',
    last_name: '',
    birth_date: '',
    gender: '',
    medical_conditions: '',
    no_medical_conditions: false
  });
  
  const [selectedClasses, setSelectedClasses] = useState([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  useEffect(() => {
    fetchClasses();
  }, []);

  const fetchClasses = async () => {
    try {
      const res = await api.get('/classes');
      setClasses(res.data || []);
    } catch (error) {
      console.error('Failed to fetch classes:', error);
      toast.error('Failed to load classes');
    } finally {
      setLoading(false);
    }
  };

  const calculateTotal = () => {
    const subtotal = selectedClasses.reduce((total, classId) => {
      const cls = classes.find(c => c.id === classId);
      return total + (cls?.term_price || 0);
    }, 0);
    return { subtotal, total: subtotal };
  };

  const toggleClass = (classId) => {
    setSelectedClasses(prev => 
      prev.includes(classId) 
        ? prev.filter(id => id !== classId)
        : [...prev, classId]
    );
  };

  const validateStep = (stepNum) => {
    if (stepNum === 1) {
      if (!guardianForm.first_name || !guardianForm.last_name || !guardianForm.email) {
        toast.error('Please fill in all required fields');
        return false;
      }
      if (!guardianForm.email.includes('@')) {
        toast.error('Please enter a valid email address');
        return false;
      }
      if (!guardianForm.password || guardianForm.password.length < 8) {
        toast.error('Password must be at least 8 characters');
        return false;
      }
      if (guardianForm.password !== guardianForm.confirm_password) {
        toast.error('Passwords do not match');
        return false;
      }
    }
    if (stepNum === 2) {
      if (!childForm.first_name || !childForm.last_name || !childForm.birth_date) {
        toast.error('Please fill in child details');
        return false;
      }
    }
    if (stepNum === 3) {
      if (selectedClasses.length === 0) {
        toast.error('Please select at least one class');
        return false;
      }
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  };

  const handleSubmit = async () => {
    if (!agreedToTerms) {
      toast.error('Please agree to the terms and conditions');
      return;
    }
    
    setSubmitting(true);
    try {
      const res = await api.post('/register/public-checkout', {
        guardian: guardianForm,
        child: {
          ...childForm,
          name: `${childForm.first_name} ${childForm.last_name}`
        },
        selected_class_ids: selectedClasses,
        origin_url: window.location.origin
      });
      
      if (res.data.checkout_url) {
        toast.success('Redirecting to secure payment...');
        setTimeout(() => {
          window.location.href = res.data.checkout_url;
        }, 1000);
      } else {
        // No payment needed (free classes)
        toast.success('Registration complete!');
        navigate('/login');
      }
    } catch (err) {
      console.error('Registration failed:', err);
      toast.error(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-3xl font-bold text-white mb-2">Register for Classes</h1>
          <p className="text-white/60">Create an account and enroll your child in our performing arts classes</p>
        </motion.div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                step >= s ? 'bg-[#FF007F] text-white' : 'bg-white/10 text-white/50'
              }`}>
                {step > s ? <CheckCircle className="w-5 h-5" /> : s}
              </div>
              {s < 4 && <div className={`w-12 h-1 mx-1 ${step > s ? 'bg-[#FF007F]' : 'bg-white/10'}`} />}
            </div>
          ))}
        </div>

        {/* Step 1: Parent/Guardian Details */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <User className="w-5 h-5 text-[#FF007F]" />
                  Your Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70">First Name *</Label>
                    <Input
                      value={guardianForm.first_name}
                      onChange={(e) => setGuardianForm({...guardianForm, first_name: e.target.value})}
                      className="bg-white/5 border-white/10 text-white mt-1"
                      placeholder="Your first name"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Last Name *</Label>
                    <Input
                      value={guardianForm.last_name}
                      onChange={(e) => setGuardianForm({...guardianForm, last_name: e.target.value})}
                      className="bg-white/5 border-white/10 text-white mt-1"
                      placeholder="Your last name"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-white/70">Email *</Label>
                  <Input
                    type="email"
                    value={guardianForm.email}
                    onChange={(e) => setGuardianForm({...guardianForm, email: e.target.value})}
                    className="bg-white/5 border-white/10 text-white mt-1"
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <Label className="text-white/70">Phone</Label>
                  <Input
                    type="tel"
                    value={guardianForm.phone}
                    onChange={(e) => setGuardianForm({...guardianForm, phone: e.target.value})}
                    className="bg-white/5 border-white/10 text-white mt-1"
                    placeholder="07xxx xxxxxx"
                  />
                </div>
                <div className="pt-4 border-t border-white/10">
                  <p className="text-white/70 text-sm mb-4">Create your account password</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-white/70">Password *</Label>
                      <Input
                        type="password"
                        value={guardianForm.password}
                        onChange={(e) => setGuardianForm({...guardianForm, password: e.target.value})}
                        className="bg-white/5 border-white/10 text-white mt-1"
                        placeholder="Min 8 characters"
                      />
                    </div>
                    <div>
                      <Label className="text-white/70">Confirm Password *</Label>
                      <Input
                        type="password"
                        value={guardianForm.confirm_password}
                        onChange={(e) => setGuardianForm({...guardianForm, confirm_password: e.target.value})}
                        className="bg-white/5 border-white/10 text-white mt-1"
                        placeholder="Confirm password"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Step 2: Child Details */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Baby className="w-5 h-5 text-[#FF007F]" />
                  Child Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70">First Name *</Label>
                    <Input
                      value={childForm.first_name}
                      onChange={(e) => setChildForm({...childForm, first_name: e.target.value})}
                      className="bg-white/5 border-white/10 text-white mt-1"
                      placeholder="Student's first name"
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Last Name *</Label>
                    <Input
                      value={childForm.last_name}
                      onChange={(e) => setChildForm({...childForm, last_name: e.target.value})}
                      className="bg-white/5 border-white/10 text-white mt-1"
                      placeholder="Student's last name"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white/70">Date of Birth *</Label>
                    <Input
                      type="date"
                      value={childForm.birth_date}
                      onChange={(e) => setChildForm({...childForm, birth_date: e.target.value})}
                      className="bg-white/5 border-white/10 text-white mt-1"
                      min={`${new Date().getFullYear() - 75}-01-01`}
                      max={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div>
                    <Label className="text-white/70">Gender</Label>
                    <Select
                      value={childForm.gender}
                      onValueChange={(value) => setChildForm({...childForm, gender: value})}
                    >
                      <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                        <SelectItem value="prefer_not_to_say">Prefer not to say</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-white/70">Medical Conditions / Allergies</Label>
                  <div className="flex items-center gap-2 mt-2 mb-2">
                    <Checkbox
                      id="no_medical"
                      checked={childForm.no_medical_conditions}
                      onCheckedChange={(checked) => setChildForm({
                        ...childForm, 
                        no_medical_conditions: checked,
                        medical_conditions: checked ? '' : childForm.medical_conditions
                      })}
                    />
                    <Label htmlFor="no_medical" className="text-white/60 text-sm cursor-pointer">
                      No known medical conditions or allergies
                    </Label>
                  </div>
                  {!childForm.no_medical_conditions && (
                    <Input
                      value={childForm.medical_conditions}
                      onChange={(e) => setChildForm({...childForm, medical_conditions: e.target.value})}
                      className="bg-white/5 border-white/10 text-white"
                      placeholder="Please list any conditions we should be aware of"
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Step 3: Select Classes */}
        {step === 3 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <GraduationCap className="w-5 h-5 text-[#FF007F]" />
                  Select Classes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-white/60 text-sm mb-4">Choose the classes you'd like to enroll in</p>
                <div className="space-y-3 max-h-[400px] overflow-y-auto">
                  {classes.map((cls) => (
                    <label
                      key={cls.id}
                      className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                        selectedClasses.includes(cls.id)
                          ? 'bg-[#FF007F]/10 border-[#FF007F]/50'
                          : 'bg-white/5 border-white/10 hover:border-white/20'
                      }`}
                    >
                      <Checkbox
                        checked={selectedClasses.includes(cls.id)}
                        onCheckedChange={() => toggleClass(cls.id)}
                      />
                      <div className="flex-1">
                        <div className="flex items-center justify-between">
                          <span className="text-white font-medium">{cls.name}</span>
                          <span className="text-[#FF007F] font-bold">{formatPrice(cls.term_price)}</span>
                        </div>
                        {cls.description && (
                          <p className="text-white/50 text-sm mt-1">{cls.description}</p>
                        )}
                        <div className="flex items-center gap-4 mt-2 text-white/40 text-xs">
                          {cls.age_min && cls.age_max && (
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              Ages {cls.age_min}-{cls.age_max}
                            </span>
                          )}
                          {cls.duration && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {cls.duration} mins
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                {selectedClasses.length > 0 && (
                  <div className="mt-4 p-3 bg-green-500/10 rounded-lg border border-green-500/30">
                    <p className="text-green-400 text-sm">
                      {selectedClasses.length} class{selectedClasses.length > 1 ? 'es' : ''} selected - Total: {formatPrice(calculateTotal().total)}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Step 4: Review & Pay */}
        {step === 4 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
            <Card className="bg-white/5 border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-[#FF007F]" />
                  Review & Pay
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Summary */}
                <div className="space-y-4">
                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-white/70 text-sm mb-2">Parent/Guardian</h4>
                    <p className="text-white">{guardianForm.first_name} {guardianForm.last_name}</p>
                    <p className="text-white/60 text-sm">{guardianForm.email}</p>
                  </div>
                  
                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-white/70 text-sm mb-2">Child</h4>
                    <p className="text-white">{childForm.first_name} {childForm.last_name}</p>
                    <p className="text-white/60 text-sm">DOB: {childForm.birth_date}</p>
                  </div>
                  
                  <div className="p-4 bg-white/5 rounded-lg">
                    <h4 className="text-white/70 text-sm mb-2">Selected Classes</h4>
                    {selectedClasses.map(classId => {
                      const cls = classes.find(c => c.id === classId);
                      return cls && (
                        <div key={classId} className="flex justify-between text-white py-1">
                          <span>{cls.name}</span>
                          <span>{formatPrice(cls.term_price)}</span>
                        </div>
                      );
                    })}
                    <div className="border-t border-white/10 mt-2 pt-2 flex justify-between text-white font-bold">
                      <span>Total</span>
                      <span className="text-[#FF007F]">{formatPrice(calculateTotal().total)}</span>
                    </div>
                  </div>
                </div>

                {/* Terms */}
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms"
                    checked={agreedToTerms}
                    onCheckedChange={setAgreedToTerms}
                  />
                  <Label htmlFor="terms" className="text-white/70 text-sm cursor-pointer leading-relaxed">
                    I confirm that I have read and agree to the{' '}
                    <a
                      href="/terms-and-conditions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[#FF007F] underline hover:no-underline"
                      data-testid="terms-link"
                    >
                      UPA Terms and Conditions
                    </a>
                    , available on the UPA website, including the six-week notice period for cancellation and UPA's policies regarding attendance, conduct, safeguarding, photography, filming and fees.
                  </Label>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          {step > 1 ? (
            <Button
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <ChevronLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => navigate('/')}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Cancel
            </Button>
          )}
          
          {step < 4 ? (
            <Button
              onClick={handleNext}
              className="bg-gradient-to-r from-[#7000FF] to-[#FF007F] hover:opacity-90"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !agreedToTerms}
              className="bg-gradient-to-r from-[#7000FF] to-[#FF007F] hover:opacity-90"
            >
              {submitting ? 'Processing...' : calculateTotal().total > 0 ? 'Proceed to Payment' : 'Complete Registration'}
            </Button>
          )}
        </div>

        {/* Already have an account? */}
        <p className="text-center text-white/50 text-sm mt-6">
          Already have an account?{' '}
          <a href="/login" className="text-[#FF007F] hover:underline">Sign in</a>
        </p>
      </div>
    </div>
  );
};

export default PublicRegister;
